import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdPerson, MdPhone, MdEdit, MdSave, MdBookmarks, MdLocalHospital, MdLogout, MdEmail, MdHome, MdCake, MdWc, MdRedeem, MdSupport } from 'react-icons/md';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout, updateUser, showToast } = useAuth();

  // Full profile fields (mirrors Flutter ProfileScreen).
  const [editing, setEditing] = useState(false);
  const [name,    setName]    = useState(user?.name    || '');
  const [age,     setAge]     = useState('');
  const [gender,  setGender]  = useState('');
  const [address, setAddress] = useState('');
  const [email,   setEmail]   = useState('');
  const [saving,  setSaving]  = useState(false);

  // Hydrate the form once on mount from the backend so age/gender/address are
  // visible even though AuthContext only stores the basics in localStorage.
  useEffect(() => {
    if (!user?.phone) return;
    api.getMe(user.phone).then(r => {
      if (r.error || !r.data) return;
      const d = r.data;
      if (d.name)    setName(d.name);
      if (d.age != null && d.age !== '') setAge(String(d.age));
      if (d.gender)  setGender(d.gender);
      if (d.address) setAddress(d.address);
      if (d.email && !d.email.includes('@hcen.app')) setEmail(d.email);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.phone]);

  if (!user) return (
    <div className="page empty fade-in">
      <div className="empty-icon">🔐</div>
      <div className="empty-title">Login to view profile</div>
      <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/login')}>
        Login / Sign Up
      </button>
    </div>
  );

  const saveProfile = async () => {
    if (!name.trim()) { showToast('Name cannot be empty', 'error'); return; }
    if (age && (isNaN(age) || +age <= 0 || +age > 130)) {
      showToast('Enter a valid age', 'error'); return;
    }
    setSaving(true);
    const updates = { name: name.trim() };
    if (age)     updates.age     = Number(age);
    if (gender)  updates.gender  = gender;
    if (address) updates.address = address.trim();
    if (email)   updates.email   = email.trim();
    const r = await api.updateMe(user.phone, updates);
    setSaving(false);
    if (r.error) { showToast('Update failed: ' + r.error, 'error'); return; }
    // Reflect in AuthContext + localStorage so other pages see the new name.
    updateUser({ name: name.trim() });
    showToast('Profile updated ✅', 'success');
    setEditing(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    showToast('Logged out', 'info');
  };

  const phone = user.phone || '';
  const initials = (user.name || phone).charAt(0).toUpperCase();

  return (
    <div className="page fade-in" style={{ maxWidth: 480 }}>

      {/* Avatar & Header */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{
          width: 80, height: 80, borderRadius: '50%', margin: '0 auto 16px',
          background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 36, fontWeight: 900, color: '#0a0f1e',
        }}>
          {initials}
        </div>
        <div style={{ fontSize: 20, fontWeight: 800 }}>{user.name || 'Patient'}</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
          HCEN Patient Account
        </div>
      </div>

      {/* Profile Card */}
      <div className="glass glass-p" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 700 }}>My Profile</div>
          {!editing ? (
            <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <MdEdit size={14} /> Edit
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={saveProfile} disabled={saving}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <MdSave size={14} /> {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}
        </div>

        {/* Phone (read-only) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
          padding: '12px 14px', background: 'var(--bg-card)', borderRadius: 10 }}>
          <MdPhone size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>PHONE NUMBER</div>
            <div style={{ fontWeight: 600, marginTop: 2 }}>{phone}</div>
          </div>
        </div>

        {/* Name (editable) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10,
          padding: '12px 14px', background: 'var(--bg-card)', borderRadius: 10 }}>
          <MdPerson size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>FULL NAME</div>
            {editing ? (
              <input className="input" value={name} onChange={e => setName(e.target.value)}
                placeholder="Your name" style={{ marginTop: 6, padding: '8px 12px', fontSize: 14 }} />
            ) : (
              <div style={{ fontWeight: 600, marginTop: 2 }}>{name || 'Not set'}</div>
            )}
          </div>
        </div>

        {/* Age */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10,
          padding: '12px 14px', background: 'var(--bg-card)', borderRadius: 10 }}>
          <MdCake size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>AGE</div>
            {editing ? (
              <input className="input" type="number" min="1" max="130" value={age}
                onChange={e => setAge(e.target.value)} placeholder="e.g. 35"
                style={{ marginTop: 6, padding: '8px 12px', fontSize: 14 }} />
            ) : (
              <div style={{ fontWeight: 600, marginTop: 2 }}>{age || 'Not set'}</div>
            )}
          </div>
        </div>

        {/* Gender */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10,
          padding: '12px 14px', background: 'var(--bg-card)', borderRadius: 10 }}>
          <MdWc size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>GENDER</div>
            {editing ? (
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                {['Male', 'Female', 'Other'].map(g => (
                  <button key={g} type="button" onClick={() => setGender(g)} style={{
                    flex: 1, padding: '8px 6px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                    border: '1px solid', cursor: 'pointer',
                    borderColor: gender === g ? 'var(--red, #d32f2f)' : 'var(--border)',
                    background: gender === g ? 'rgba(211,47,47,0.08)' : 'transparent',
                    color: gender === g ? 'var(--red, #d32f2f)' : 'var(--text-muted)',
                  }}>{g}</button>
                ))}
              </div>
            ) : (
              <div style={{ fontWeight: 600, marginTop: 2 }}>{gender || 'Not set'}</div>
            )}
          </div>
        </div>

        {/* Address */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10,
          padding: '12px 14px', background: 'var(--bg-card)', borderRadius: 10 }}>
          <MdHome size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>ADDRESS</div>
            {editing ? (
              <input className="input" value={address} onChange={e => setAddress(e.target.value)}
                placeholder="Street, City" style={{ marginTop: 6, padding: '8px 12px', fontSize: 14 }} />
            ) : (
              <div style={{ fontWeight: 600, marginTop: 2 }}>{address || 'Not set'}</div>
            )}
          </div>
        </div>

        {/* Email */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 14px', background: 'var(--bg-card)', borderRadius: 10 }}>
          <MdEmail size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>EMAIL</div>
            {editing ? (
              <input className="input" type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com" style={{ marginTop: 6, padding: '8px 12px', fontSize: 14 }} />
            ) : (
              <div style={{ fontWeight: 600, marginTop: 2 }}>{email || 'Not set'}</div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="glass glass-p" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 14 }}>Quick Actions</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            { icon: <MdBookmarks size={18} />,      label: 'My Bookings',    sub: 'View all appointments', path: '/bookings' },
            { icon: <MdRedeem size={18} />,          label: 'Refer & Earn',   sub: 'Invite friends, get ₹50 each', path: '/refer' },

            { icon: <MdSupport size={18} />,         label: 'Help & Support', sub: 'Raise a ticket or chat', path: '/support' },
            { icon: <MdLocalHospital size={18} />,   label: 'Find Hospitals', sub: 'Discover nearby hospitals', path: '/hospitals' },
          ].map(item => (
            <button key={item.path} onClick={() => navigate(item.path)} style={{
              display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px',
              background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12,
              cursor: 'pointer', color: 'var(--text)', textAlign: 'left', transition: 'var(--transition)',
              width: '100%',
            }}>
              <div style={{ color: 'var(--primary)' }}>{item.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{item.label}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{item.sub}</div>
              </div>
              <span style={{ color: 'var(--text-muted)' }}>›</span>
            </button>
          ))}
        </div>
      </div>

      {/* App Info */}
      <div className="glass glass-p" style={{ marginBottom: 20 }}>
        <div style={{ fontWeight: 700, marginBottom: 12 }}>About HCEN</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.7 }}>
          <div style={{ marginBottom: 6 }}>🏥 Healthcare Emergency Network</div>
          <div style={{ marginBottom: 6 }}>📱 Available on Android App + Web</div>
          <div style={{ marginBottom: 6 }}>✉️ Support: <a href="mailto:support@hcen.in" style={{ color: 'var(--primary)', fontWeight: 600 }}>support@hcen.in</a></div>
          <div style={{ marginBottom: 10 }}>🌐 hcen.in</div>
          <div style={{
            paddingTop: 10,
            borderTop: '1px dashed var(--border)',
            fontSize: 12,
            color: 'var(--text-muted)',
            lineHeight: 1.5,
          }}>
            <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>
              Operated by
            </div>
            <div>Palithya Simha Industries Private Limited</div>
          </div>
        </div>
      </div>

      {/* Logout */}
      <button className="btn btn-full btn-lg" onClick={handleLogout}
        style={{
          background: 'rgba(239,68,68,0.1)', color: 'var(--danger)',
          border: '1px solid rgba(239,68,68,0.25)', borderRadius: 14,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
        <MdLogout size={18} /> Sign Out
      </button>
    </div>
  );
}
